/**
 * CatalogAdmin — admin-facing catalog manager native to the investment
 * module. Replaces the Settings → Investment Catalog tab as the primary
 * surface for editing loops / steps / account-kinds.
 *
 * Loop-first design: the headline view is a visual grid of loop cards
 * (hero logo + name + active pill + facts strip + step count). Clicking a
 * card opens the shared LoopDetailsPanel — same one investors see on
 * Home — but with admin-edit power for name, description, active toggle,
 * hero logo, and the headline facts.
 *
 * Steps + Kinds are secondary tabs with inline CRUD (no separate
 * modals — they're light enough to fit in a table row).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabase } from '../lib/supabase.js';
import { CostEditModal } from '../components/CostLinesCard.jsx';
import { cycleSteps, deriveVelocityDays, labelLoopSteps, maxEconomics } from '../lib/useInvestmentData.js';
import './home.css';
import './catalog-admin.css';
import {
  LoopDetailsPanel,
  LoopHeroLogo,
  LoopFactsStrip,
  kindColor,
  kindInitial,
} from './Home.jsx';

const fmt$ = (n) => '$' + (Math.round(Number(n) || 0)).toLocaleString();
const fmt$Big = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000) return '$' + (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + 'K';
  return '$' + v.toLocaleString();
};
const fmtPct = (n) => (Number(n || 0) * 100).toFixed(2).replace(/\.?0+$/, '') + '%';

// D23 step fee. Wise adds its fee ON TOP of the load (charge = load × 1.0125),
// and the engine charges fee_rate × the movement amount, which is the CARD
// CHARGE. So the fee is entered as % of the load and stored on the charge:
// f = r / (1 + r) — 1.25% of the load is 0.012345679… of the charge. Never
// round the stored value: 1.23% would short every fee.
const feeOnLoad   = (f) => Number(f) / (1 - Number(f));                 // stored → % of load (fraction)
const feeLoadPct  = (f) => Math.round(feeOnLoad(f) * 1e6) / 1e4;         // 0.012345679 → 1.25
const feeOnCharge = (loadPct) => { const r = Number(loadPct) / 100; return r / (1 + r); };

// Aggregate "at max throughput" stats across a list of active shaped
// loops — what the catalog would produce if everything ran flat-out.
// Returns null when no loops have enough data (max + velocity + rate)
// to compute economics. Otherwise: totals + a blended APY (computed
// from the totals, not averaged across loops).
function aggregateMaxStats(activeLoops) {
  if (!Array.isArray(activeLoops) || !activeLoops.length) return null;
  let capital = 0, gross = 0, cost = 0, net = 0, volume = 0, eligibleCount = 0;
  for (const lp of activeLoops) {
    const e = maxEconomics(lp);
    if (!e) continue;
    eligibleCount += 1;
    capital += e.capital;
    gross   += e.gross;
    cost    += e.cost;
    net     += e.net;
    volume  += e.volumeMonthly;
  }
  if (eligibleCount === 0) return null;
  // Blended APY = annualized net return on the combined capital base.
  // Computed from the aggregate so it's a true portfolio APY, not an
  // arithmetic mean of per-loop APYs (which would be misleading when
  // loops have very different capital scales).
  const apy = capital > 0 ? (net * 12) / capital : 0;
  return { capital, gross, cost, net, volume, apy, eligibleCount };
}

export default function CatalogAdmin({ profile, navigate, params }) {
  const isAdmin = profile?.role === 'admin';
  const [tab, setTab] = useState('loops'); // 'loops' | 'steps' | 'kinds' | 'tkc'
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [loops, setLoops] = useState([]);
  const [steps, setSteps] = useState([]);
  const [refs, setRefs] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [holders, setHolders] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [membersAll, setMembersAll] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [detailsLoop, setDetailsLoop] = useState(null);
  const [newLoopEditing, setNewLoopEditing] = useState(false); // open panel in edit mode for fresh inserts
  const [loopFilter, setLoopFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [flash, setFlash] = useState(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) { setStatus('error'); setError(new Error('Supabase not available')); return; }
      setStatus('loading');
      try {
        const [loopsRes, stepsRes, refsRes, kindsRes, assignRes, holdersRes, acctRes, membersRes] = await Promise.all([
          sb.from('loops').select('*').order('created_at', { ascending: false }),
          sb.from('steps').select('*').order('source_kind', { ascending: true }),
          sb.from('loop_step_refs').select('*').order('step_order', { ascending: true }),
          sb.from('inv_account_kinds').select('*').order('sort_order', { ascending: true }).order('name', { ascending: true }),
          sb.from('loop_assignments').select('*'),
          // Account holders only — sub-accounts can't be assigned loops.
          sb.from('members').select('id,name,display_name,managed_by_member_id,active').is('managed_by_member_id', null).eq('active', true).order('name'),
          // Kinds tab: every financial account + every member (sub-accounts
          // included) so each kind can list who actually holds one.
          sb.from('financial_accounts').select('id,account_kind,member_id,active,taekus_card_uuid,nickname,created_at'),
          sb.from('members').select('id,name,display_name'),
        ]);
        if (cancelled) return;
        setLoops(loopsRes.data || []);
        setSteps(stepsRes.data || []);
        setRefs(refsRes.data || []);
        setKinds(kindsRes.data || []);
        setAssignments(assignRes.data || []);
        setHolders(holdersRes.data || []);
        setAccounts(acctRes.data || []);
        setMembersAll(membersRes.data || []);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(e); }
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  // Reshape loops + steps + refs + kinds into something the side panel can
  // render. Mirrors what useInvestmentData / AdminOverview do, minus
  // per-investor allocations/events.
  const shapedLoops = useMemo(() => {
    const stepsById = Object.fromEntries(steps.map((s) => [s.id, s]));
    const kindMeta = Object.fromEntries(kinds.map((k) => [k.name, k]));
    const kindLabel = (slug) => (slug && (kindMeta[slug]?.label || slug.replace(/_/g, ' '))) || '';
    const kindLogo = (slug) => (slug && kindMeta[slug]?.logo_url) || null;
    return loops.map((l) => {
      const myRefs = refs.filter((r) => r.loop_id === l.id).sort((a, b) => a.step_order - b.step_order);
      const shapedSteps = myRefs.map((r) => {
        const s = stepsById[r.step_id] || {};
        return {
          id: s.id,
          idx: r.step_order,
          source: kindLabel(s.source_kind),
          sourceKind: s.source_kind || '',
          sourceLogo: kindLogo(s.source_kind),
          dest: kindLabel(s.dest_kind),
          destKind: s.dest_kind || '',
          destLogo: kindLogo(s.dest_kind),
          rate: Number(s.value_rate || 0),
          feeRate: s.fee_rate == null ? 0 : Number(s.fee_rate), // D23 per-txn fee (fraction)
          amount: s.amount == null ? null : Number(s.amount),
          stepDurationDays: Number(s.step_duration_days || 0),
          stepDurationIsBusiness: !!s.step_duration_is_business,
          waitDurationDays: Number(s.wait_duration_days || 0),
          waitDurationIsBusiness: !!s.wait_duration_is_business,
          movesYTD: 0,
          valueYTD: 0,
          executed: {},
        };
      });
      return {
        id: l.id,
        name: l.name,
        description: l.description || '',
        active: l.active,
        heroLogoUrl:    l.hero_logo_url || null,
        maxThroughput:  l.max_throughput == null ? null : Number(l.max_throughput),
        maxThroughputUnit: l.max_throughput_unit || 'per_cycle',
        // Auto-derived from step durations — see deriveVelocityDays.
        velocityDays:   deriveVelocityDays(shapedSteps),
        rateOfReturn:   l.rate_of_return == null ? null : Number(l.rate_of_return),
        devicesNeeded:  l.devices_needed == null ? null : Number(l.devices_needed),
        // Loop-level operating cost — used by the catalog max-throughput
        // economics. Exposed here so the LoopDetailsPanel admin form
        // shows the value when it loads.
        monthlyOperatingCost: Number(l.monthly_operating_cost || 0),
        paymentMethods: Array.isArray(l.payment_methods) ? l.payment_methods : [],
        // Present (possibly '') only on this surface — LoopDetailsPanel
        // shows the theme editor exactly when the field is defined.
        themeColor:     l.theme_color || '',
        steps: labelLoopSteps(shapedSteps),
        cyclesCompleted: 0,
        cyclesInProgress: 0,
        valueYTD: 0,
        blendedRate: cycleSteps(shapedSteps).reduce((s, st) => s + Number(st.rate || 0), 0),
        allocByMember: {},
      };
    });
  }, [loops, steps, refs, kinds]);

  const visibleLoops = shapedLoops.filter((l) => {
    if (loopFilter === 'active') return l.active;
    if (loopFilter === 'inactive') return !l.active;
    return true;
  });

  // "+ New loop" creates a blank inactive row, then opens the side panel
  // in edit mode so the admin can name it before publishing.
  const createLoop = async () => {
    const sb = getSupabase();
    if (!sb) return;
    const { data, error: err } = await sb.from('loops').insert({
      name: 'Untitled loop',
      description: '',
      active: false,
      payment_methods: [],
    }).select('*').single();
    if (err) { showFlash('Couldn\'t create loop: ' + (err.message || err)); return; }
    setReloadKey((k) => k + 1);
    setNewLoopEditing(true);
    // We need the shaped version, but reload hasn't fired yet. Insert a
    // minimal shape inline so the panel can open immediately.
    setDetailsLoop({
      id: data.id,
      name: data.name,
      description: data.description || '',
      active: data.active,
      heroLogoUrl: null,
      maxThroughput: null,
      velocityDays: null,
      rateOfReturn: null,
      devicesNeeded: null,
      paymentMethods: [],
      themeColor: '',
      steps: [],
      cyclesCompleted: 0, cyclesInProgress: 0, valueYTD: 0, blendedRate: 0,
      allocByMember: {},
    });
  };

  const showFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  };

  const onLoopUpdated = () => { setDetailsLoop(null); setNewLoopEditing(false); reload(); };
  const onLoopDeleted = () => { setDetailsLoop(null); setNewLoopEditing(false); showFlash('Loop deleted.'); reload(); };

  // Keep the open panel synced with reloaded data so step/assignment edits
  // refresh in place instead of going stale (or closing the panel).
  const openLoop = detailsLoop ? (shapedLoops.find((l) => l.id === detailsLoop.id) || detailsLoop) : null;

  // Deep link: #catalog-admin?loopId=… opens that loop's panel — the
  // catalog's admin "Manage →" button arrives this way.
  const openedFromParamRef = useRef(false);
  useEffect(() => {
    if (openedFromParamRef.current) return;
    if (status !== 'ready' || !params?.loopId) return;
    const lp = shapedLoops.find((l) => l.id === params.loopId);
    if (lp) { setDetailsLoop(lp); openedFromParamRef.current = true; }
  }, [status, params?.loopId, shapedLoops]);

  if (!isAdmin) {
    return (
      <div className="app density-comfortable"><main className="page">
        <div className="card" style={{ borderColor: 'var(--rose-border)', background: 'var(--rose-bg)', color: 'var(--rose)' }}>
          <strong>Admin only.</strong>
          <p style={{ margin: '6px 0 0' }}>Catalog management is restricted to admins.</p>
        </div>
      </main></div>
    );
  }
  if (status === 'loading') {
    return <div className="app density-comfortable"><main className="page"><div className="card" style={{ color: 'var(--text-2)' }}>Loading catalog…</div></main></div>;
  }
  if (status === 'error') {
    return (
      <div className="app density-comfortable"><main className="page">
        <div className="card" style={{ borderColor: 'var(--rose-border)', background: 'var(--rose-bg)', color: 'var(--rose)' }}>
          <strong>Couldn't load catalog.</strong>
          <p className="mono" style={{ fontSize: 12, marginTop: 8 }}>{String(error?.message || error)}</p>
        </div>
      </main></div>
    );
  }

  const counts = {
    loops: loops.length,
    active: loops.filter((l) => l.active).length,
    steps: steps.length,
    kinds: kinds.length,
  };

  // Aggregate "at max throughput" stats across active loops — drives the
  // hero card at the top of the page. Null when no active loop has the
  // data (max + velocity + rate) needed to compute economics.
  const activeShapedLoops = shapedLoops.filter((l) => l.active);
  const aggMax = aggregateMaxStats(activeShapedLoops);

  return (
    <div className="app density-comfortable">
      <main className="page">
        <div className="page-title-row">
          <div>
            <div className="page-eyebrow"><span className="dot"></span> Investment · Catalog admin</div>
            <h1 className="page-title">Catalog</h1>
            <p className="page-sub">
              The library of <strong>{counts.loops}</strong> loop{counts.loops === 1 ? '' : 's'} ({counts.active} active) built on{' '}
              <strong>{counts.steps}</strong> step{counts.steps === 1 ? '' : 's'} across{' '}
              <strong>{counts.kinds}</strong> account kind{counts.kinds === 1 ? '' : 's'}.
            </p>
          </div>
          <div className="page-actions">
            <button type="button" className="btn-ghost" onClick={() => navigate && navigate('home')}>← Back</button>
          </div>
        </div>

        {/* "At max throughput" hero — shows what the active catalog produces
            if every loop ran flat-out at its max_throughput cap. Surfaces
            the capital wall, blended APY, and the dollar flow through TK
            biz/personal cards so admin can plan KYC and card capacity. */}
        {aggMax && (
          <section className="cadm-hero">
            <div className="cadm-hero-eyebrow">
              <span className="cadm-hero-dot"></span>
              At max throughput · {aggMax.eligibleCount} active loop{aggMax.eligibleCount === 1 ? '' : 's'}
            </div>
            <div className="cadm-hero-headline">
              <div className="cadm-hero-headline-num">{fmt$(aggMax.capital)}</div>
              <div className="cadm-hero-headline-lbl">capital needed to run everything simultaneously</div>
            </div>
            <div className="cadm-hero-grid">
              <div className="cadm-hero-stat">
                <div className="cadm-hero-stat-lbl">Net earn / mo</div>
                <div className="cadm-hero-stat-val cadm-hero-stat--good">{fmt$(aggMax.net)}</div>
                <div className="cadm-hero-stat-sub">gross {fmt$(aggMax.gross)} − cost {fmt$(aggMax.cost)}</div>
              </div>
              <div className="cadm-hero-stat">
                <div className="cadm-hero-stat-lbl">Blended APY</div>
                <div className="cadm-hero-stat-val cadm-hero-stat--good">{fmtPct(aggMax.apy)}</div>
                <div className="cadm-hero-stat-sub">on combined capital base</div>
              </div>
              <div className="cadm-hero-stat">
                <div className="cadm-hero-stat-lbl">Total volume / mo</div>
                <div className="cadm-hero-stat-val">{fmt$Big(aggMax.volume)}</div>
                <div className="cadm-hero-stat-sub">dollars cycling through all loops</div>
              </div>
              <div className="cadm-hero-stat">
                <div className="cadm-hero-stat-lbl">Operating cost / mo</div>
                <div className="cadm-hero-stat-val cadm-hero-stat--warn">{fmt$(aggMax.cost)}</div>
                <div className="cadm-hero-stat-sub">sum of loops' monthly_operating_cost</div>
              </div>
            </div>
          </section>
        )}

        {flash && <div className="cadm-flash">{flash}</div>}

        {/* Tabs */}
        <div className="cadm-tabs">
          <button type="button" className={'cadm-tab' + (tab === 'loops' ? ' is-active' : '')} onClick={() => setTab('loops')}>
            Loops <span className="cadm-tab-count">{counts.loops}</span>
          </button>
          <button type="button" className={'cadm-tab' + (tab === 'steps' ? ' is-active' : '')} onClick={() => setTab('steps')}>
            Steps <span className="cadm-tab-count">{counts.steps}</span>
          </button>
          <button type="button" className={'cadm-tab' + (tab === 'kinds' ? ' is-active' : '')} onClick={() => setTab('kinds')}>
            Account kinds <span className="cadm-tab-count">{counts.kinds}</span>
          </button>
          <button type="button" className={'cadm-tab' + (tab === 'tkc' ? ' is-active' : '')} onClick={() => setTab('tkc')}>
            TK-C cards <span className="cadm-tab-count">{accounts.filter((a) => a.account_kind === 'taekus_corp').length}</span>
          </button>
          <button type="button" className={'cadm-tab' + (tab === 'costs' ? ' is-active' : '')} onClick={() => setTab('costs')}>
            Costs
          </button>
        </div>

        {tab === 'loops' && (
          <LoopsTab
            loops={visibleLoops}
            allLoops={shapedLoops}
            filter={loopFilter}
            setFilter={setLoopFilter}
            onOpen={(lp) => { setNewLoopEditing(false); setDetailsLoop(lp); }}
            onCreate={createLoop}
          />
        )}
        {tab === 'steps' && (
          <StepsTab steps={steps} kinds={kinds} refs={refs} loops={loops} onChange={reload} flash={showFlash} />
        )}
        {tab === 'kinds' && (
          <KindsTab kinds={kinds} steps={steps} refs={refs} loops={loops} accounts={accounts} membersAll={membersAll} onChange={reload} flash={showFlash} />
        )}
        {tab === 'tkc' && (
          <TkcTab accounts={accounts} membersAll={membersAll} onChange={reload} flash={showFlash} />
        )}
        {tab === 'costs' && (
          <CostsTab flash={showFlash} />
        )}
      </main>

      {openLoop && (
        <LoopDetailsPanel
          loop={openLoop}
          members={[]}
          isAdmin={true}
          navigate={navigate}
          defaultEditing={newLoopEditing}
          adminTools={{ steps, kinds, refs, assignments, holders, onChanged: reload }}
          onLoopUpdated={onLoopUpdated}
          onLoopDeleted={onLoopDeleted}
          onClose={() => { setDetailsLoop(null); setNewLoopEditing(false); }}
        />
      )}
    </div>
  );
}

// ─── Loops tab ────────────────────────────────────────────────────────
function LoopsTab({ loops, allLoops, filter, setFilter, onOpen, onCreate }) {
  return (
    <>
      <div className="cadm-toolbar">
        <div className="cadm-filter-chips">
          {[
            { id: 'all',      label: `All · ${allLoops.length}` },
            { id: 'active',   label: `Active · ${allLoops.filter((l) => l.active).length}` },
            { id: 'inactive', label: `Inactive · ${allLoops.filter((l) => !l.active).length}` },
          ].map((f) => (
            <button key={f.id} type="button" className={'cadm-chip' + (filter === f.id ? ' is-active' : '')} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" className="btn-primary cadm-new-btn" onClick={onCreate}>+ New loop</button>
      </div>

      {loops.length === 0 ? (
        <div className="cadm-empty">
          No loops match this filter.
          {allLoops.length === 0 && <> <button type="button" className="btn-link" onClick={onCreate}>Add your first loop →</button></>}
        </div>
      ) : (
        <div className="cadm-loop-grid">
          {loops.map((lp) => (
            <CatalogLoopCard key={lp.id} loop={lp} onClick={() => onOpen(lp)} />
          ))}
        </div>
      )}
    </>
  );
}

function CatalogLoopCard({ loop, onClick }) {
  return (
    <div
      className={'cadm-loop-card' + (loop.active ? '' : ' is-inactive')}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      <div className="cadm-loop-head">
        <LoopHeroLogo loop={loop} size={48} />
        <div className="cadm-loop-title">
          <div className="cadm-loop-eyebrow">
            <span className={'cadm-status-pill ' + (loop.active ? 'is-active' : 'is-inactive')}>{loop.active ? 'Active' : 'Inactive'}</span>
            <span className="cadm-loop-steps">{loop.steps.length}-step</span>
          </div>
          <div className="cadm-loop-name">{loop.name || 'Untitled loop'}</div>
        </div>
      </div>
      {loop.description && <p className="cadm-loop-desc">{loop.description}</p>}
      <LoopFactsStrip loop={loop} />
    </div>
  );
}

// ─── Steps tab ────────────────────────────────────────────────────────
function StepsTab({ steps, kinds, refs, loops, onChange, flash }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const stepInLoops = useMemo(() => {
    const map = {};
    refs.forEach((r) => { (map[r.step_id] = map[r.step_id] || []).push(r.loop_id); });
    return map;
  }, [refs]);
  const loopNames = useMemo(() => Object.fromEntries(loops.map((l) => [l.id, l.name])), [loops]);
  const activeKinds = kinds.filter((k) => k.active);

  return (
    <>
      <div className="cadm-toolbar">
        <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
          {steps.length} step{steps.length === 1 ? '' : 's'} in the catalog. One step per <code className="mono">(source, dest)</code> pair.
        </div>
        <button type="button" className="btn-primary cadm-new-btn" onClick={() => setCreating(true)}>+ New step</button>
      </div>

      {creating && (
        <StepEditor
          step={null}
          kinds={activeKinds}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); onChange(); flash('Step created.'); }}
          flash={flash}
        />
      )}

      <div className="cadm-table-wrap">
        <table className="cadm-table">
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th>Rate</th>
              <th>Fee</th>
              <th>Amount</th>
              <th>Step takes</th>
              <th>Wait after</th>
              <th>Status</th>
              <th>In loops</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {steps.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>No steps yet.</td></tr>
            ) : steps.map((s) => editing === s.id ? (
              <tr key={s.id}><td colSpan={9} style={{ padding: 0 }}>
                <StepEditor
                  step={s}
                  kinds={activeKinds}
                  onCancel={() => setEditing(null)}
                  onSaved={() => { setEditing(null); onChange(); flash('Step saved.'); }}
                  flash={flash}
                />
              </td></tr>
            ) : (
              <StepRow
                key={s.id}
                step={s}
                kinds={kinds}
                inLoopIds={stepInLoops[s.id] || []}
                loopNames={loopNames}
                onEdit={() => setEditing(s.id)}
                onToggle={onChange}
                onDeleted={onChange}
                flash={flash}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StepRow({ step, kinds, inLoopIds, loopNames, onEdit, onToggle, onDeleted, flash }) {
  const [saving, setSaving] = useState(false);
  const kindLabel = (slug) => kinds.find((k) => k.name === slug)?.label || slug;
  const isActive = step.status === 'active';
  const amount = step.amount == null ? 'Max' : '$' + Math.round(Number(step.amount)).toLocaleString();
  const dur = (d, biz) => {
    const n = Number(d || 0);
    if (!n) return 'Instant';
    return `${n} ${biz ? 'biz' : 'cal'} day${n === 1 ? '' : 's'}`;
  };

  const toggle = async () => {
    setSaving(true);
    const sb = getSupabase();
    const { error } = await sb.from('steps').update({ status: isActive ? 'inactive' : 'active' }).eq('id', step.id);
    setSaving(false);
    if (error) { flash('Toggle failed: ' + (error.message || error)); return; }
    onToggle();
  };
  const del = async () => {
    if (inLoopIds.length > 0) {
      flash(`Can't delete — used in ${inLoopIds.length} loop(s). Remove from those first.`);
      return;
    }
    if (!confirm(`Delete step ${kindLabel(step.source_kind)} → ${kindLabel(step.dest_kind)}? This can't be undone.`)) return;
    setSaving(true);
    const sb = getSupabase();
    const { error } = await sb.from('steps').delete().eq('id', step.id);
    setSaving(false);
    if (error) { flash('Delete failed: ' + (error.message || error)); return; }
    flash('Step deleted.');
    onDeleted();
  };

  return (
    <tr className={isActive ? '' : 'is-inactive'}>
      <td><KindChip kind={step.source_kind} kinds={kinds} /></td>
      <td><KindChip kind={step.dest_kind} kinds={kinds} /></td>
      <td className="mono">{fmtPct(step.value_rate)}{step.bonus_per_txn != null && Number(step.bonus_per_txn) > 0 && <span style={{ color: 'var(--text-3)', fontSize: 11 }} title={`Plus a flat $${Number(step.bonus_per_txn).toFixed(2)} per transaction (T-035)`}> +{Math.round(Number(step.bonus_per_txn) * 100)}¢</span>}</td>
      <td className="mono" title={step.fee_rate != null && Number(step.fee_rate) > 0 ? `Fee as % of the load, added on top of each charge (stored as ${(Number(step.fee_rate) * 100).toFixed(4)}% of the charge)` : 'No per-transaction fee'}>{step.fee_rate != null && Number(step.fee_rate) > 0 ? fmtPct(feeOnLoad(step.fee_rate)) : '—'}</td>
      <td className="mono">{amount}</td>
      <td>{dur(step.step_duration_days, step.step_duration_is_business)}</td>
      <td>{dur(step.wait_duration_days, step.wait_duration_is_business)}</td>
      <td><span className={'cadm-status-pill ' + (isActive ? 'is-active' : 'is-inactive')}>{isActive ? 'Active' : 'Inactive'}</span></td>
      <td>
        {inLoopIds.length === 0 ? <span style={{ color: 'var(--text-3)' }}>—</span> : (
          <div className="cadm-step-loops">
            {inLoopIds.slice(0, 3).map((id) => <span key={id} className="cadm-step-loop">{loopNames[id] || id}</span>)}
            {inLoopIds.length > 3 && <span className="cadm-step-loop">+{inLoopIds.length - 3}</span>}
          </div>
        )}
      </td>
      <td className="cadm-row-actions">
        <button type="button" className="cadm-link" disabled={saving} onClick={onEdit}>Edit</button>
        <button type="button" className="cadm-link" disabled={saving} onClick={toggle}>{isActive ? 'Deactivate' : 'Activate'}</button>
        <button type="button" className="cadm-link cadm-link-danger" disabled={saving} onClick={del}>Delete</button>
      </td>
    </tr>
  );
}

function KindChip({ kind, kinds }) {
  const meta = kinds.find((k) => k.name === kind);
  const color = kindColor(kind);
  return (
    <span className="cadm-kind-chip" style={{ '--chip-color': color }}>
      <span className="cadm-kind-chip-tile">
        {meta?.logo_url
          ? <img src={meta.logo_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.textContent = kindInitial(meta?.label || kind); }} />
          : kindInitial(meta?.label || kind)}
      </span>
      <span className="cadm-kind-chip-label">{meta?.label || kind}</span>
    </span>
  );
}

function StepEditor({ step, kinds, onCancel, onSaved, flash }) {
  const isNew = !step;
  const [sourceKind, setSourceKind] = useState(step?.source_kind || (kinds[0]?.name || ''));
  const [destKind,   setDestKind]   = useState(step?.dest_kind   || (kinds[0]?.name || ''));
  const [rate,       setRate]       = useState(step ? String(Math.round(Number(step.value_rate || 0) * 10000) / 100) : ''); // % UI
  // D23 (T-022): per-transaction fee, entered as % of the LOAD, stored on the
  // charge (see feeOnCharge). Blank = no fee.
  const [fee,        setFee]        = useState(step?.fee_rate == null ? '' : String(feeLoadPct(step.fee_rate)));
  // T-035: flat $ bonus per posted movement (Taekus pays ~10 pts/txn → 0.10).
  const [bonus,      setBonus]      = useState(step?.bonus_per_txn == null ? '' : String(step.bonus_per_txn));
  const [amount,     setAmount]     = useState(step?.amount == null ? '' : String(step.amount));
  const [isMax,      setIsMax]      = useState(step ? step.amount == null : true);
  const [stepDays,   setStepDays]   = useState(String(step?.step_duration_days || 0));
  const [stepBiz,    setStepBiz]    = useState(!!step?.step_duration_is_business);
  const [instant,    setInstant]    = useState(!!step && Number(step.step_duration_days || 0) === 0);
  const [waitDays,   setWaitDays]   = useState(String(step?.wait_duration_days || 0));
  const [waitBiz,    setWaitBiz]    = useState(!!step?.wait_duration_is_business);
  const [notes,      setNotes]      = useState(step?.notes || '');
  const [saving,     setSaving]     = useState(false);

  const save = async () => {
    if (!sourceKind || !destKind) { flash('Pick a source and destination kind.'); return; }
    if (sourceKind === destKind) { flash('Source and destination must differ.'); return; }
    setSaving(true);
    const sb = getSupabase();
    const payload = {
      source_kind: sourceKind,
      dest_kind: destKind,
      value_rate: rate === '' ? 0 : Number(rate) / 100,
      // Effective-dated by construction: the engine snapshots fee_rate onto
      // each event at post time, so editing this touches future movements only.
      fee_rate: fee === '' ? null : feeOnCharge(fee),
      // T-035: same effective-dating — the bonus is snapshotted per event.
      bonus_per_txn: bonus === '' ? null : Number(bonus),
      amount: isMax ? null : (amount === '' ? null : Number(amount)),
      step_duration_days: instant ? 0 : (parseInt(stepDays || '0', 10) || 0),
      step_duration_is_business: !!stepBiz,
      wait_duration_days: parseInt(waitDays || '0', 10) || 0,
      wait_duration_is_business: !!waitBiz,
      notes: notes.trim() || null,
    };
    const { error } = isNew
      ? await sb.from('steps').insert({ ...payload, status: 'active' })
      : await sb.from('steps').update(payload).eq('id', step.id);
    setSaving(false);
    if (error) { flash('Save failed: ' + (error.message || error)); return; }
    onSaved();
  };

  return (
    <div className="cadm-editor">
      <div className="cadm-editor-grid">
        <label><span>Source kind</span>
          <select value={sourceKind} onChange={(e) => setSourceKind(e.target.value)} disabled={saving}>
            {kinds.map((k) => <option key={k.name} value={k.name}>{k.label}</option>)}
          </select>
        </label>
        <label><span>Dest kind</span>
          <select value={destKind} onChange={(e) => setDestKind(e.target.value)} disabled={saving}>
            {kinds.map((k) => <option key={k.name} value={k.name}>{k.label}</option>)}
          </select>
        </label>
        <label><span>Rate (%)</span>
          <input onWheel={(e) => e.currentTarget.blur()} type="number" step="0.01" min="0" value={rate} onChange={(e) => setRate(e.target.value)} disabled={saving} placeholder="e.g. 2.2" />
        </label>
        <label><span>Fee (% of the load, added on top)</span>
          <input onWheel={(e) => e.currentTarget.blur()} type="number" step="any" min="0" max="99" value={fee} onChange={(e) => setFee(e.target.value)} disabled={saving} placeholder="e.g. 1.25 — blank = none" title="Enter the fee as a % of the amount loaded (Wise: 1.25). The card is charged load + fee, so it is stored on the charge (1.25% of the load = 1.2346% of the charge). The profit pool sees value minus fee, and settlement pays the fee back to the investor out of the reward (D23). Editing applies to future movements only." />
        </label>
        <label><span>Bonus per transaction ($)</span>
          <input onWheel={(e) => e.currentTarget.blur()} type="number" step="0.01" min="0" value={bonus} onChange={(e) => setBonus(e.target.value)} disabled={saving} placeholder="e.g. 0.10 — blank = none" title="Flat dollars added to value on every movement posted against this step (T-035: Taekus pays ~10 points per transaction regardless of size). Snapshotted per event — editing applies to future movements only." />
        </label>
        <label><span>Amount ($)</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input onWheel={(e) => e.currentTarget.blur()} type="number" min="0" step="100" value={isMax ? '' : amount} onChange={(e) => setAmount(e.target.value)} disabled={saving || isMax} placeholder="5000" style={{ flex: 1 }} />
            <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
              <input type="checkbox" checked={isMax} onChange={(e) => setIsMax(e.target.checked)} disabled={saving} /> Max
            </label>
          </div>
        </label>
        <label><span>Step takes</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input onWheel={(e) => e.currentTarget.blur()} type="number" min="0" value={instant ? '0' : stepDays} onChange={(e) => setStepDays(e.target.value)} disabled={saving || instant} style={{ flex: 1 }} />
            <select value={stepBiz ? 'biz' : 'cal'} onChange={(e) => setStepBiz(e.target.value === 'biz')} disabled={saving || instant}>
              <option value="cal">cal days</option><option value="biz">biz days</option>
            </select>
            <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={instant} onChange={(e) => { setInstant(e.target.checked); if (e.target.checked) setStepDays('0'); }} disabled={saving} /> Instant
            </label>
          </div>
        </label>
        <label><span>Wait after</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <input onWheel={(e) => e.currentTarget.blur()} type="number" min="0" value={waitDays} onChange={(e) => setWaitDays(e.target.value)} disabled={saving} style={{ flex: 1 }} />
            <select value={waitBiz ? 'biz' : 'cal'} onChange={(e) => setWaitBiz(e.target.value === 'biz')} disabled={saving}>
              <option value="cal">cal days</option><option value="biz">biz days</option>
            </select>
          </div>
        </label>
        <label style={{ gridColumn: '1 / -1' }}><span>Notes (optional)</span>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={saving} placeholder="Short description" />
        </label>
      </div>
      <div className="cadm-editor-actions">
        <button type="button" className="btn-ghost" disabled={saving} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : (isNew ? 'Create step' : 'Save changes')}</button>
      </div>
    </div>
  );
}

// ─── Costs tab (T-022 / D19) — monthly cost attestation grid ─────────────
// Costs bill only as confirmed monthly facts. Each cell is a (member, month):
//   • bold figure   = recognized fact (admin/app/bot-attested) — in the books
//   • grey "assumed"= the old model's assumption (source 'cutover') — kept as
//     reference/prefill ONLY, ignored by every sum until Kareem records the
//     actual ("ignore the past, since it is assumed", 2026-08-08)
//   • "adj" tag     = a D20 late answer for a closed month, shown under the
//     month it RELATES to (stored in the open month it was answered in)
// Recording goes through v2_record_cost_attestation, which handles the
// closed-month redirect itself. Manual until the agent era automates the ask.
function CostsTab({ flash }) {
  const [households, setHouseholds] = useState([]); // [{holder, name, members:[{id,name}]}]
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // D-15: ONE cost store — line items (every status) + suggestion schedules.
  // The attestation slot grid is retired (COST_MODULE_REDESIGN_PLAN.md).
  const [items, setItems] = useState(null);
  const [schedules, setSchedules] = useState([]);
  // Owner (2026-08-22): the ledger takes filters + sorting. Assumed history
  // lives inside the status filter (default view excludes it, as before).
  const [flt, setFlt] = useState({ household: '', member: '', paidBy: '', status: '' });
  const [sort, setSort] = useState({ key: 'created', dir: 'desc' });
  const [memberNames, setMemberNames] = useState({});
  const [editing, setEditing] = useState(null);   // T-051: the line being edited
  const [form, setForm] = useState({
    household: '', member: '', date: new Date().toISOString().slice(0, 10),
    amount: '', note: '', paidBy: 'investor',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) return;
      const [enrollRes, memRes, itemsRes, schedRes] = await Promise.all([
        sb.from('module_enrollment').select('member_id, enabled').eq('module_name', 'investment'),
        sb.from('members').select('id, name, display_name, managed_by_member_id, active'),
        sb.from('investment_cost_items').select('*').order('created_at', { ascending: false }),
        sb.from('investment_cost_schedules').select('*').order('created_at', { ascending: false }),
      ]);
      if (cancelled) return;
      const members = memRes.data || [];
      const byId = Object.fromEntries(members.map((m) => [m.id, m]));
      const hh = (enrollRes.data || []).map((e) => {
        const holder = byId[e.member_id];
        const fam = [e.member_id, ...members.filter((m) => m.managed_by_member_id === e.member_id && m.active).map((m) => m.id)];
        return {
          holder: e.member_id,
          enabled: e.enabled,
          name: holder ? (holder.display_name || holder.name) : e.member_id,
          members: fam.map((id) => ({ id, name: byId[id] ? (byId[id].display_name || byId[id].name) : id })),
        };
      }).sort((a, b) => a.name.localeCompare(b.name));
      setHouseholds(hh);
      setItems(itemsRes.data || []);
      setSchedules(schedRes.data || []);
      setMemberNames(Object.fromEntries(members.map((m) => [m.id, m.display_name || m.name])));
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const monthLabel = (iso) => new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

  // D-14: review an investor-submitted item. Confirm books it; reject is
  // terminal (T-027) with the optional note kept on the row. One prompt max.
  const reviewItem = async (it, action) => {
    const sb = getSupabase();
    if (!sb) return;
    let note = null;
    if (action === 'reject') {
      note = prompt(`Reject this $${Number(it.amount).toLocaleString()} cost ("${it.note}")? Optional note to the investor:`, '');
      if (note == null) return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_review_cost_item', { p_item_id: it.id, p_action: action, p_note: note || null });
      if (error) throw error;
      flash(action === 'confirm' ? 'Cost confirmed — it is in the books now.' : 'Cost rejected — kept as a record, never counted.');
      setReloadKey((k) => k + 1);
    } catch (e) {
      flash('Review failed: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  // D-15: act on a SUGGESTED occurrence from the ledger (the red cards on
  // Home/AdminOverview are the primary surface; this is the admin's list-side
  // handle). Confirm books; skip records the zero.
  const confirmOccurrence = async (it) => {
    const sb = getSupabase();
    if (!sb) return;
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_confirm_cost_occurrence', { p_item_id: it.id, p_amount: null });
      if (error) throw error;
      flash('Charge confirmed — it is in the books now.');
      setReloadKey((k) => k + 1);
    } catch (e) { flash('Confirm failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // Also answers an assumed-history month with "confirmed: did not pay"
  // (reference → skipped clears the provisional flag for that month).
  const skipOccurrence = async (it) => {
    const sb = getSupabase();
    if (!sb) return;
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_skip_cost_occurrence', { p_item_id: it.id, p_note: null });
      if (error) throw error;
      flash(it.status === 'reference' ? 'Assumed month answered: did not pay — reference cleared.' : 'Recorded: the charge did not happen.');
      setReloadKey((k) => k + 1);
    } catch (e) { flash('Skip failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // T-051: admin corrections. Remove takes a line out of the books (status
  // removed, reason kept); an investor's requested change on a booked line is
  // confirmed (applied) or declined (discarded) here or on the overview.
  const removeItem = async (it) => {
    const sb = getSupabase();
    if (!sb) return;
    const reason = prompt(`Remove this $${Number(it.amount).toLocaleString()} line ("${it.note}") for ${memberNames[it.member_id] || it.member_id}? It leaves the books; the reason stays on the line.`, '');
    if (reason == null) return;
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_admin_remove_cost_item', { p_item_id: it.id, p_reason: reason || null });
      if (error) throw error;
      flash('Line removed — out of the books, kept as a record.');
      setReloadKey((k) => k + 1);
    } catch (e) { flash('Remove failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };
  const reviewEdit = async (it, action) => {
    const sb = getSupabase();
    if (!sb) return;
    let note = null;
    if (action === 'reject') {
      note = prompt(`Decline the change to $${Number(it.pending_edit?.amount).toLocaleString()} on "${it.note}"? The line stays at $${Number(it.amount).toLocaleString()}. Optional note to the investor:`, '');
      if (note == null) return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_review_cost_edit', { p_item_id: it.id, p_action: action, p_note: note || null });
      if (error) throw error;
      flash(action === 'confirm' ? 'Change confirmed — the line is updated in the books.' : 'Change declined — the line keeps its figure.');
      setReloadKey((k) => k + 1);
    } catch (e) { flash('Review failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // D-15: schedule lifecycle (pause / resume / retire) + the approval rail.
  const schedLifecycle = async (s, status) => {
    const sb = getSupabase();
    if (!sb) return;
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_upsert_cost_schedule', {
        p_member_id: s.member_id, p_name: s.name, p_amount: s.amount, p_bill_day: s.bill_day,
        p_id: s.id, p_status: status,
      });
      if (error) throw error;
      flash(`Schedule ${status === 'active' ? 'resumed' : status}.`);
      setReloadKey((k) => k + 1);
    } catch (e) { flash('Update failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  const reviewSchedule = async (s, action) => {
    const sb = getSupabase();
    if (!sb) return;
    let note = null;
    if (action === 'reject') {
      note = prompt(`Reject "${s.name}" ($${Number(s.amount).toLocaleString()}/mo for ${memberNames[s.member_id] || s.member_id})? Optional note:`, '');
      if (note == null) return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc('v2_review_cost_schedule', { p_schedule_id: s.id, p_action: action, p_note: note || null });
      if (error) throw error;
      flash(action === 'approve' ? 'Schedule approved — its confirm card starts on the next bill day.' : 'Schedule rejected.');
      setReloadKey((k) => k + 1);
    } catch (e) { flash('Review failed: ' + (e?.message || e)); }
    finally { setSaving(false); }
  };

  // T-034: record a one-time cost line item through the audited RPC.
  const addItem = async () => {
    const sb = getSupabase();
    if (!sb) return;
    const hh = households.find((h) => h.holder === form.household);
    if (!hh) { flash('Pick a household.'); return; }
    const isShared = form.member === '__household__';
    const memberId = isShared ? hh.holder : form.member;   // D-1: whole-household books to the head
    if (!memberId) { flash('Pick a member.'); return; }
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) { flash('Enter a positive amount.'); return; }
    if (!form.note.trim()) { flash('A note is required — what was bought, and for whom.'); return; }
    setSaving(true);
    try {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) { flash('Pick the date it happened.'); setSaving(false); return; }
      const { data, error } = await sb.rpc('v2_record_cost_item', {
        p_member_id: memberId,
        p_month: form.date.slice(0, 7) + '-01',
        p_occurred_on: form.date,
        p_amount: amt,
        p_note: (isShared ? '(household) ' : '') + form.note.trim(),
        p_paid_by: form.paidBy,
        p_source: 'admin',
      });
      if (error) throw error;
      flash(data?.postedAsAdjustment
        ? `Recorded $${amt} — that month is closed, so it posted into the open month as a dated adjustment.`
        : `Recorded $${amt} for ${isShared ? hh.name + ' (household)' : (memberNames[memberId] || memberId)}.`);
      setForm((f) => ({ ...f, amount: '', note: '' }));
      setReloadKey((k) => k + 1);
    } catch (e) {
      flash('Record failed: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)' };
  const inp = { font: 'inherit', padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', color: 'var(--text-1)' };

  if (items == null) return <div style={{ padding: '18px 0', color: 'var(--text-3)' }}>Loading cost lines…</div>;

  // Filter, then sort. Month sorts by the month the line RELATES to; entered
  // by creation time (the default, newest first).
  const famIds = flt.household
    ? new Set((households.find((h) => h.holder === flt.household)?.members || []).map((m) => m.id))
    : null;
  const sortVal = (it) => {
    switch (sort.key) {
      case 'month': return it.relates_to_month || it.month || '';
      case 'member': return (memberNames[it.member_id] || it.member_id || '').toLowerCase();
      case 'amount': return Number(it.amount || 0);
      case 'paidBy': return it.paid_by || '';
      case 'status': return it.status || 'confirmed';
      default: return it.created_at || '';
    }
  };
  const visible = (items || [])
    .filter((it) => (flt.status === 'edit-requested' ? !!it.pending_edit : flt.status ? (it.status || 'confirmed') === flt.status : it.status !== 'reference'))
    .filter((it) => !famIds || famIds.has(it.member_id))
    .filter((it) => !flt.member || it.member_id === flt.member)
    .filter((it) => !flt.paidBy || it.paid_by === flt.paidBy)
    .sort((a, b) => {
      const va = sortVal(a); const vb = sortVal(b);
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  const Th = ({ k, right, children }) => (
    <th
      style={{ textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
      title="Click to sort"
    >{children}{sort.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}</th>
  );

  return (
    <div>
      {/* ── T-034: one-time & extra costs — append-only line items ─────────── */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', margin: '4px 0 20px', background: 'var(--surface)' }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 4 }}>One-time & extra costs</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 10px', maxWidth: 720 }}>
          Every cost is a <strong>line that adds up</strong> — one-time entries here, recurring charges via the
          schedules below (D-15: a schedule only <em>suggests</em>; each month's charge books when its red card is
          confirmed). A whole-household cost books to the head (D-1). <strong>Who paid decides the effect (D-13):</strong>
          investor-paid reduces what they owe you; Kareem-paid only slows the profit clock. Investor-submitted entries
          land <strong>pending</strong> until you confirm them (D-14).
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={lbl}>Household
            <select value={form.household} onChange={(e) => setForm((f) => ({ ...f, household: e.target.value, member: '' }))} style={inp}>
              <option value="">—</option>
              {households.map((h) => <option key={h.holder} value={h.holder}>{h.name}</option>)}
            </select>
          </label>
          <label style={lbl}>Member
            <select value={form.member} onChange={(e) => setForm((f) => ({ ...f, member: e.target.value }))} style={inp} disabled={!form.household}>
              <option value="">—</option>
              {(households.find((h) => h.holder === form.household)?.members || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              {form.household && <option value="__household__">Whole household (books to {households.find((h) => h.holder === form.household)?.members[0]?.name})</option>}
            </select>
          </label>
          <label style={lbl}>Date
            <input type="date" value={form.date} min="2026-04-01" onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} style={inp} />
          </label>
          <label style={lbl}>Amount $
            <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} style={{ ...inp, width: 110 }} />
          </label>
          <label style={lbl}>Paid by
            <select value={form.paidBy} onChange={(e) => setForm((f) => ({ ...f, paidBy: e.target.value }))} style={inp}>
              <option value="investor">Investor</option>
              <option value="kareem">Kareem</option>
            </select>
          </label>
          <label style={{ ...lbl, flex: 1, minWidth: 220 }}>Note (required)
            <input type="text" value={form.note} placeholder="e.g. phone for Mom" onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} style={{ ...inp, width: '100%', boxSizing: 'border-box' }} />
          </label>
          <button type="button" className="btn-primary-sm" disabled={saving} onClick={addItem}>＋ Record cost</button>
        </div>
        {(items || []).length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>Filter</span>
            <select value={flt.household} onChange={(e) => setFlt((f) => ({ ...f, household: e.target.value, member: '' }))} style={inp}>
              <option value="">All households</option>
              {households.map((h) => <option key={h.holder} value={h.holder}>{h.name}</option>)}
            </select>
            <select value={flt.member} onChange={(e) => setFlt((f) => ({ ...f, member: e.target.value }))} style={inp} disabled={!flt.household}>
              <option value="">All members</option>
              {(households.find((h) => h.holder === flt.household)?.members || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select value={flt.paidBy} onChange={(e) => setFlt((f) => ({ ...f, paidBy: e.target.value }))} style={inp}>
              <option value="">Paid by · all</option>
              <option value="investor">Investor</option>
              <option value="kareem">Kareem</option>
            </select>
            <select value={flt.status} onChange={(e) => setFlt((f) => ({ ...f, status: e.target.value }))} style={inp}>
              <option value="">All statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending review</option>
              <option value="suggested">Awaiting confirmation</option>
              <option value="skipped">Didn't happen</option>
              <option value="rejected">Rejected</option>
              <option value="removed">Removed</option>
              <option value="reference">Assumed history</option>
              <option value="edit-requested">Change requested</option>
            </select>
            {(flt.household || flt.member || flt.paidBy || flt.status) && (
              <button type="button" className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setFlt({ household: '', member: '', paidBy: '', status: '' })}>Clear</button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
              {visible.length} line{visible.length === 1 ? '' : 's'} · ${visible.reduce((s, it) => s + Number(it.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
        {(items || []).length > 0 && visible.length === 0 && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-3)' }}>No cost lines match the filters.</div>
        )}
        {visible.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table className="cadm-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <Th k="month">Month</Th>
                  <Th k="member">Member</Th>
                  <Th k="amount" right>Amount</Th>
                  <Th k="paidBy">Paid by</Th>
                  <Th k="status">Status</Th>
                  <th>Note</th>
                  <Th k="created">Entered</Th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((it) => {
                  const status = it.status || 'confirmed';
                  return (
                  <tr key={it.id} className={'cost-row cost-row--' + status + (it.pending_edit ? ' has-edit' : '')} style={(status === 'rejected' || status === 'reference' || status === 'skipped' || status === 'removed') ? { opacity: 0.62 } : undefined}>
                    <td>{monthLabel(it.relates_to_month || it.month)}{it.relates_to_month ? <span style={{ fontSize: 10, color: 'var(--amber)' }}> adj in {monthLabel(it.month)}</span> : null}</td>
                    <td>{memberNames[it.member_id] || it.member_id}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, textDecoration: (status === 'rejected' || status === 'removed') ? 'line-through' : 'none' }}>
                      ${Number(it.amount).toLocaleString()}
                      {it.pending_edit ? <div style={{ fontSize: 11, color: 'var(--accent-2, #3730a3)', fontWeight: 700 }} title={it.pending_edit.reason || undefined}>→ ${Number(it.pending_edit.amount).toLocaleString()} requested</div> : null}
                    </td>
                    <td>{it.paid_by === 'kareem' ? <span style={{ color: 'var(--amber)', fontWeight: 700 }}>Kareem</span> : 'investor'}</td>
                    <td>
                      {status === 'pending' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'var(--amber)', fontWeight: 700, fontSize: 12 }}>pending</span>
                          <button type="button" className="btn-primary-sm" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => reviewItem(it, 'confirm')}>Confirm</button>
                          <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px', color: 'var(--rose)' }} onClick={() => reviewItem(it, 'reject')}>Reject</button>
                        </span>
                      ) : status === 'rejected' ? (
                        <span style={{ color: 'var(--rose)', fontWeight: 700, fontSize: 12 }} title={it.review_note || undefined}>rejected — not counted</span>
                      ) : status === 'suggested' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#b91c1c', fontWeight: 800, fontSize: 12 }}>awaiting confirmation</span>
                          <button type="button" className="btn-primary-sm" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => confirmOccurrence(it)}>Confirm charge</button>
                          <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => skipOccurrence(it)}>Didn't happen</button>
                        </span>
                      ) : status === 'removed' ? (
                        <span style={{ color: 'var(--text-3)', fontWeight: 700, fontSize: 12 }} title={it.review_note || undefined}>removed — not counted</span>
                      ) : it.pending_edit ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'var(--accent-2, #3730a3)', fontWeight: 800, fontSize: 12 }}>change requested</span>
                          <button type="button" className="btn-primary-sm cost-edit-confirm" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => reviewEdit(it, 'confirm')}>Confirm change</button>
                          <button type="button" className="btn-ghost cost-edit-decline" disabled={saving} style={{ fontSize: 11, padding: '2px 8px', color: 'var(--rose)' }} onClick={() => reviewEdit(it, 'reject')}>Decline</button>
                        </span>
                      ) : status === 'skipped' ? (
                        <span style={{ color: 'var(--text-3)', fontWeight: 700, fontSize: 12 }} title={it.review_note || undefined}>did not happen — $0</span>
                      ) : status === 'reference' ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: 'var(--text-3)', fontStyle: 'italic', fontSize: 12 }}>assumed — reference only</span>
                          <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => skipOccurrence(it)} title="Answer this assumed month: the member did not pay — clears the provisional flag">Did not pay</button>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-3)', fontSize: 12 }}>confirmed</span>
                      )}
                    </td>
                    <td style={{ maxWidth: 340 }}>{it.note}{it.pending_edit && it.pending_edit.note !== it.note ? <div style={{ fontSize: 11, color: 'var(--accent-2, #3730a3)' }}>requested note: {it.pending_edit.note}</div> : null}{it.review_note ? <div style={{ fontSize: 11, color: 'var(--text-3)' }}>review: {it.review_note}</div> : null}</td>
                    <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{(it.created_at || '').slice(0, 10)}{it.source === 'app' ? <div style={{ fontSize: 10.5 }}>by investor</div> : null}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {(status === 'confirmed' || status === 'pending' || status === 'suggested') && (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button type="button" className="btn-ghost cost-row-edit" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setEditing(it)}>Edit</button>
                          <button type="button" className="btn-ghost cost-row-remove" disabled={saving} style={{ fontSize: 11, padding: '2px 8px', color: 'var(--rose)' }} onClick={() => removeItem(it)}>Remove</button>
                        </span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <CostEditModal item={editing} mode="admin" onClose={() => setEditing(null)} onSaved={() => { setEditing(null); flash('Line updated — the previous values stay on it as an audit note.'); setReloadKey((k) => k + 1); }} />
      )}

      {/* ── D-15: recurring costs — suggestion schedules ──────────────────── */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', margin: '0 0 20px', background: 'var(--surface)' }}>
        <div style={{ fontWeight: 800, fontSize: 13.5, marginBottom: 4 }}>Recurring costs · schedules</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 10px', maxWidth: 720 }}>
          A schedule never books money. On its bill day it fires a <strong style={{ color: '#b91c1c' }}>red confirm card</strong> to
          whoever pays (investor-paid → their Home; Kareem-paid → your overview), and only the confirmation books the
          month's line. Create schedules from the <strong>＋ Log a cost</strong> button on the admin overview.
        </p>
        {schedules.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '6px 0' }}>No schedules yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="cadm-table" style={{ minWidth: 640 }}>
              <thead>
                <tr><th>Member</th><th>Name</th><th style={{ textAlign: 'right' }}>$/mo</th><th>Bill day</th><th>Paid by</th><th>Status</th><th>Next due</th><th></th></tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr key={s.id} style={s.status === 'retired' ? { opacity: 0.55 } : undefined}>
                    <td>{memberNames[s.member_id] || s.member_id}</td>
                    <td>{s.name}{s.note ? <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.note}</div> : null}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>${Number(s.amount).toLocaleString()}</td>
                    <td>{s.bill_day}</td>
                    <td>{s.paid_by === 'kareem' ? <span style={{ color: 'var(--amber)', fontWeight: 700 }}>Kareem</span> : 'investor'}</td>
                    <td>
                      {s.status === 'pending_approval'
                        ? <span style={{ color: 'var(--amber)', fontWeight: 700, fontSize: 12 }}>awaiting your approval</span>
                        : <span style={{ fontSize: 12, fontWeight: 700, color: s.status === 'active' ? 'var(--green)' : 'var(--text-3)' }}>{s.status}</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-3)' }}>{s.status === 'active' ? s.next_due : '—'}</td>
                    <td>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        {s.status === 'pending_approval' && (
                          <>
                            <button type="button" className="btn-primary-sm" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => reviewSchedule(s, 'approve')}>Approve</button>
                            <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px', color: 'var(--rose)' }} onClick={() => reviewSchedule(s, 'reject')}>Reject</button>
                          </>
                        )}
                        {s.status === 'active' && (
                          <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => schedLifecycle(s, 'paused')}>Pause</button>
                        )}
                        {s.status === 'paused' && (
                          <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => schedLifecycle(s, 'active')}>Resume</button>
                        )}
                        {(s.status === 'active' || s.status === 'paused') && (
                          <button type="button" className="btn-ghost" disabled={saving} style={{ fontSize: 11, padding: '2px 8px', color: 'var(--rose)' }} onClick={() => schedLifecycle(s, 'retired')}>Retire</button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Kinds tab ────────────────────────────────────────────────────────
// ─── Draw-recording panel (T-009 Build 1, owner decisions 2026-08-08) ──────
// Admin controls for the taekus-draw-fetch pipeline: the fetch-state line
// (cadence heartbeat + unmapped-card backlog), a Check-now poke, and the
// kind-level merchant-pattern table that drives step suggestions
// (decision 1 — 'X*' = prefix match, otherwise substring; lower priority
// wins; suggestions only ever pre-fill, the confirmer commits).
function DrawRecordingPanel() {
  const sb = getSupabase();
  const [state, setState] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [kindList, setKindList] = useState([]);
  const [draft, setDraft] = useState({ kind_name: '', pattern: '', priority: '100' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!sb) return;
    const [st, pats, ks] = await Promise.all([
      sb.from('taekus_draw_fetch_state').select('*').eq('id', 1).maybeSingle(),
      sb.from('merchant_kind_patterns').select('*').order('kind_name').order('priority'),
      sb.from('inv_account_kinds').select('name, label').order('sort_order'),
    ]);
    setState(st.data || null);
    setPatterns(pats.data || []);
    const list = ks.data || [];
    setKindList(list);
    setDraft((d) => (d.kind_name || !list.length ? d : { ...d, kind_name: list[0].name }));
  }, [sb]);
  useEffect(() => { load(); }, [load]);

  const poke = async () => {
    if (!sb || busy) return;
    setBusy(true); setMsg(null);
    try {
      const { data: sess } = await sb.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch('/.netlify/functions/taekus-draw-poke', {
        method: 'POST', headers: { Authorization: 'Bearer ' + token },
      });
      const body = await res.json().catch(() => null);
      if (res.status === 429) setMsg(`Cooldown — try again in ${body?.retryAfterSeconds || 60}s`);
      else if (!res.ok) setMsg(((body && body.error) || 'Check failed') + (body?.detail ? ` — ${body.detail}` : ''));
      else setMsg(body?.summary || 'Checked');
    } catch { setMsg('Check failed'); }
    setBusy(false);
    await load();
  };

  const addPattern = async () => {
    if (!sb || !draft.kind_name || !draft.pattern.trim()) return;
    setBusy(true);
    const { error } = await sb.from('merchant_kind_patterns').insert({
      kind_name: draft.kind_name,
      pattern: draft.pattern.trim(),
      priority: Number(draft.priority) || 100,
    });
    if (error) setMsg(error.message);
    else setDraft((d) => ({ ...d, pattern: '' }));
    setBusy(false);
    await load();
  };

  const delPattern = async (id) => {
    if (!sb) return;
    setBusy(true);
    await sb.from('merchant_kind_patterns').delete().eq('id', id);
    setBusy(false);
    await load();
  };

  const kindLabelOf = (name) => (kindList.find((k) => k.name === name)?.label) || name;
  const age = state?.last_run_at
    ? Math.max(0, Math.round((Date.now() - new Date(state.last_run_at).getTime()) / 60000))
    : null;
  const inputSm = { padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border, #cbd5e1)', fontSize: 12.5 };

  return (
    <div style={{ margin: '14px 0', padding: '12px 14px', border: '1px solid var(--border, #e5e7ef)', borderRadius: 12, background: 'var(--bg-2, #f8fafc)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 13.5, fontWeight: 800 }}>Draw recording</div>
          <div style={{ fontSize: 12, color: 'var(--text-3, #64748b)', marginTop: 2 }}>
            {state == null
              ? 'Not live yet — apply migrations/2026-08-08-t009-draw-recording.sql.'
              : <>
                  {age == null ? 'No fetch has run yet' : age < 1 ? 'Checked just now' : `Checked ${age} min ago`}
                  {state.last_run_via ? ` (${state.last_run_via})` : ''}
                  {state.last_result ? ` · ${state.last_result}` : ''}
                  {state.unmapped_open > 0 ? ` · ⚠ ${state.unmapped_open} transaction${state.unmapped_open === 1 ? '' : 's'} on unmapped cards — map the cards below and the next fetch picks them up` : ''}
                </>}
          </div>
        </div>
        <button type="button" className="btn-ghost" disabled={busy || state == null} onClick={poke}>
          {busy ? 'Checking…' : 'Check now'}
        </button>
        {msg && <span style={{ fontSize: 12, color: 'var(--text-3, #64748b)' }}>{msg}</span>}
      </div>

      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3, #94a3b8)', marginBottom: 6 }}>
          Merchant patterns → account kind (suggestions only)
        </div>
        {patterns.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3, #94a3b8)', marginBottom: 6 }}>
            No patterns yet — the migration seeds PayPal/Coinbase/Wise/Skrill defaults.
          </div>
        )}
        {patterns.map((p) => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border, #eef2f7)', fontSize: 12.5 }}>
            <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, minWidth: 110 }}>{p.pattern}</span>
            <span style={{ color: 'var(--text-3, #64748b)' }}>→ {kindLabelOf(p.kind_name)}</span>
            <span style={{ color: 'var(--text-3, #94a3b8)', fontSize: 11 }}>prio {p.priority}</span>
            <button type="button" className="btn-ghost" style={{ marginLeft: 'auto', fontSize: 11.5, padding: '3px 8px' }} disabled={busy} onClick={() => delPattern(p.id)}>
              Remove
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <input style={{ ...inputSm, width: 150 }} placeholder="Pattern (PP* = prefix)" value={draft.pattern}
            onChange={(e) => setDraft((d) => ({ ...d, pattern: e.target.value }))} />
          <select style={inputSm} value={draft.kind_name} onChange={(e) => setDraft((d) => ({ ...d, kind_name: e.target.value }))}>
            {kindList.map((k) => <option key={k.name} value={k.name}>{k.label}</option>)}
          </select>
          <input style={{ ...inputSm, width: 70 }} type="number" title="Priority (lower wins)" value={draft.priority}
            onChange={(e) => setDraft((d) => ({ ...d, priority: e.target.value }))} />
          <button type="button" className="btn-ghost" disabled={busy || !draft.pattern.trim()} onClick={addPattern}>
            Add pattern
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TK-C cards tab (T-009) ────────────────────────────────────────────────
// Maps each member's per-person TK-C (taekus_corp) account to its Taekus
// virtual-card UUID — "the card IS the attribution for pool draws" (D22).
// The card list comes from the read-only Taekus proxy at
// /.netlify/functions/taekus-read (admin-gated; the API key never reaches the
// browser). Assignment writes financial_accounts.taekus_card_uuid, which has
// a partial unique index so one card can never back two accounts.
//
// Suggestive matching: Kareem named the Taekus cards similarly to the app's
// member names, so once the card list loads, each unmapped account gets a
// scored suggestion (exact > whole-token > substring > bigram similarity,
// assigned greedily so no card is suggested twice). Suggestions only fill
// the input — a human Save is always the commit.

// The TK-C pool's card account: "Corporate Purchasing" (…2134). Pinned
// because Taekus's default-account fallback is broken for this login — the
// 2026-08-07 diagnostics showed "No card account for this user" on default
// lookups while explicit cardAccountUuid resolves all four accounts. Same
// constant exported as TAEKUS_POOL_CARD_ACCOUNT_UUID from
// investment-v2/src/lib/taekus.js for future evidence surfaces.
const TAEKUS_POOL_CARD_ACCOUNT = 'bd9b3e9e-9d3d-4663-b899-d1a64db5ba7f';

// Saving a mapping calls onChange -> parent reload(), which swaps the whole
// screen to its loading state and UNMOUNTS this tab — discarding the freshly
// loaded Taekus card list and any unsaved drafts after every single Save.
// Both survive at module scope instead, so a remount re-seeds them.
let tkcCardsCache = null;
let tkcDraftsCache = {};

// Score + greedily assign card→account suggestions. rows carry {id,
// memberName, taekus_card_uuid}; cards carry {uuid, name, label}. Returns
// { [accountId]: { uuid, label, s } } for unmapped accounts only; cards
// already mapped to ANY account are never suggested.
export function suggestCardMatches(rows, cards) {
  const norm = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const bigrams = (s) => {
    const t = norm(s).replace(/ /g, '');
    const out = [];
    for (let i = 0; i < t.length - 1; i++) out.push(t.slice(i, i + 2));
    return out;
  };
  const dice = (a, b) => {
    const A = bigrams(a); const B = bigrams(b);
    if (!A.length || !B.length) return 0;
    const pool = [...B];
    let hits = 0;
    for (const g of A) {
      const i = pool.indexOf(g);
      if (i >= 0) { hits++; pool.splice(i, 1); }
    }
    return (2 * hits) / (A.length + B.length);
  };
  const score = (member, card) => {
    const m = norm(member); const c = norm(card);
    if (!m || !c) return 0;
    if (m === c) return 1;
    if (c.split(' ').includes(m) || m.split(' ').includes(c)) return 0.92;
    if (c.includes(m) || m.includes(c)) return 0.8;
    return dice(m, c);
  };
  const taken = new Set(rows.map((r) => r.taekus_card_uuid).filter(Boolean));
  const free = (cards || []).filter((c) => c.uuid && !taken.has(c.uuid));
  const cands = [];
  rows.filter((r) => !r.taekus_card_uuid).forEach((r) => {
    free.forEach((c) => {
      const s = score(r.memberName, c.name || c.label);
      if (s >= 0.5) cands.push({ accountId: r.id, uuid: c.uuid, label: c.label, s });
    });
  });
  cands.sort((a, b) => b.s - a.s);
  const byAccount = {};
  const used = new Set();
  for (const c of cands) {
    if (byAccount[c.accountId] || used.has(c.uuid)) continue;
    byAccount[c.accountId] = c;
    used.add(c.uuid);
  }
  return byAccount;
}

function TkcTab({ accounts = [], membersAll = [], onChange, flash }) {
  const sb = getSupabase();
  const memberById = useMemo(() => Object.fromEntries(membersAll.map((m) => [m.id, m])), [membersAll]);
  // One row per TK-C account, not per member: an investor who holds two
  // Taekus cards (Ibrahim, 2026-09-22) gets a second account row via "Add a
  // second card" below. The OLDEST row is the member's primary — draw
  // representation books every card's draws against it (ensureAccount in
  // taekus-draw-core picks the oldest), so an extra row only ever carries a
  // card UUID and never any movements.
  const rows = useMemo(() => {
    const tkc = accounts
      .filter((a) => a.account_kind === 'taekus_corp')
      .map((a) => ({
        ...a,
        memberName: memberById[a.member_id]?.display_name || memberById[a.member_id]?.name || a.member_id,
      }));
    const oldestByMember = {};
    tkc.forEach((a) => {
      const cur = oldestByMember[a.member_id];
      if (!cur || String(a.created_at || '') < String(cur.created_at || '')) oldestByMember[a.member_id] = a;
    });
    return tkc
      .map((a) => ({ ...a, isExtra: oldestByMember[a.member_id]?.id !== a.id }))
      .sort((x, y) => String(x.memberName).localeCompare(String(y.memberName))
        || String(x.created_at || '').localeCompare(String(y.created_at || '')));
  }, [accounts, memberById]);

  const [drafts, setDraftsRaw] = useState(tkcDraftsCache);   // account id -> uuid text being edited
  const [cards, setCardsRaw] = useState(tkcCardsCache);      // [{uuid,name,label}] from the Taekus API
  const [cardsErr, setCardsErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);     // diagnostics report text
  const [diagBusy, setDiagBusy] = useState(false);
  // Setters mirror into the module-scope caches (see note above them).
  const setDrafts = (updater) => setDraftsRaw((d) => {
    const n = typeof updater === 'function' ? updater(d) : updater;
    tkcDraftsCache = n;
    return n;
  });
  const setCards = (updater) => setCardsRaw((c) => {
    const n = typeof updater === 'function' ? updater(c) : updater;
    tkcCardsCache = n;
    return n;
  });

  // Name-similarity suggestions, computed once the card list is loaded.
  const suggestions = useMemo(
    () => (cards && cards.length ? suggestCardMatches(rows, cards) : {}),
    [cards, rows],
  );
  const unfilled = (id) => drafts[id] === undefined || drafts[id] === '';
  const suggestionsToFill = Object.entries(suggestions).filter(([id]) => unfilled(id));
  const filledRows = rows.filter((a) => {
    const v = drafts[a.id];
    return v !== undefined && v.trim() && v.trim() !== (a.taekus_card_uuid || '');
  });

  // Authenticated GET through the read-only Taekus proxy. Shared by the card
  // loader and the diagnostics probe below.
  const taekusRead = async (path, params) => {
    const { data: sess } = await sb.auth.getSession();
    const token = sess?.session?.access_token;
    if (!token) throw new Error('No Supabase session.');
    const qs = new URLSearchParams({ path, ...(params || {}) });
    const res = await fetch('/.netlify/functions/taekus-read?' + qs.toString(),
      { headers: { Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      // Surface the proxy's pass-through detail (the first chunk of
      // Taekus's own body) — "Taekus returned 400" alone is undebuggable.
      const detail = body?.detail ? ' — ' + String(body.detail).slice(0, 200) : '';
      throw new Error((body?.error || ('Taekus proxy returned HTTP ' + res.status)) + detail);
    }
    return body;
  };

  // Deep probe (admin debugging, T-009): dump what this credential CAN see,
  // then retry every allowlisted card-account endpoint with each UUID the
  // internal-accounts payload exposes — tests whether a specific account
  // (e.g. the Corporate one) is addressable even though the "default card
  // account" lookup returns "No card account for this user". Read-only by
  // construction: every call goes through the same allowlisted proxy.
  const runDiagnostics = async () => {
    setDiagBusy(true); setDiag(null);
    const out = [];
    const line = (s) => { out.push(s); setDiag(out.join('\n')); };
    const attempt = async (label, path, params) => {
      try { return { ok: true, body: await taekusRead(path, params) }; }
      catch (e) { line(label + ': ' + (e?.message || e)); return { ok: false }; }
    };
    try {
      line('Taekus API diagnostics — ' + new Date().toISOString());
      const ia = await attempt('internal-accounts/', '/api/banking/internal-accounts/');
      if (ia.ok) {
        const raw = JSON.stringify(ia.body, null, 1);
        line('internal-accounts/ raw response:');
        line(raw.length > 1800 ? raw.slice(0, 1800) + ' …[truncated]' : raw);
      }
      const uuids = [...new Set((JSON.stringify(ia.body || '')
        .match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi) || []))].slice(0, 4);
      line('');
      line(uuids.length
        ? 'Candidate account UUIDs found: ' + uuids.join(', ')
        : 'No UUIDs found in the internal-accounts response.');
      for (const u of uuids) {
        line('');
        line('— trying cardAccountUuid=' + u);
        const acct = await attempt('  account/', '/api/banking/account/', { cardAccountUuid: u });
        if (acct.ok) {
          const b = acct.body || {};
          line('  account/: OK — ' + ([b.nickname, b.account_type, b.category_type].filter(Boolean).join(' · ') || 'unnamed') +
            (b.balance ? ' · available ' + b.balance.available_balance : ' · NO balance object'));
          // Live account/ responses omit documented fields for this login
          // (the balance object among them) — keep the raw head visible so
          // shape drift is diagnosable without another build.
          const raw = JSON.stringify(b);
          line('  account/ raw: ' + (raw.length > 600 ? raw.slice(0, 600) + ' …[truncated]' : raw));
        }
        const pc = await attempt('  payment-cards/virtual/', '/api/banking/payment-cards/virtual/', { cardAccountUuid: u });
        if (pc.ok) {
          const list = Array.isArray(pc.body) ? pc.body : (pc.body?.results || pc.body?.cards || pc.body?.data || []);
          line('  payment-cards/virtual/: OK — ' + list.length + ' card(s)' + (list.length
            ? ': ' + list.slice(0, 8).map((c) => (c.nickname || c.uuid || '?') + (c.last_four ? ' ·' + c.last_four : '')).join(', ')
            : ''));
        }
        const tr = await attempt('  transfers/', '/api/banking/transfers/', { cardAccountUuid: u });
        if (tr.ok) line('  transfers/: OK — ' + ((tr.body?.transfers || []).length) + ' in the 90-day window');
      }
      if (!uuids.length) {
        const tr = await attempt('transfers/ (no uuid)', '/api/banking/transfers/');
        if (tr.ok) line('transfers/ (no uuid): OK — ' + ((tr.body?.transfers || []).length) + ' transfers');
      }
      line('');
      line('Done.');
    } finally {
      setDiagBusy(false);
    }
  };

  // Card-activity probe (T-009 cleanup): list the pool account's recent
  // transactions with each row's card and — via the saved card→member
  // mapping — the member whose card moved. Built to identify the two
  // 2026-08-03 draws objectively; also a working preview of the
  // draw-recording feature's core read.
  const showActivity = async () => {
    setDiagBusy(true); setDiag(null);
    const out = [];
    const line = (s) => { out.push(s); setDiag(out.join('\n')); };
    try {
      const end = new Date();
      const start = new Date(end.getTime() - 30 * 24 * 3600 * 1000);
      line('Pool card activity — last 30 days, per Taekus');
      const byUuid = Object.fromEntries(rows.filter((r) => r.taekus_card_uuid).map((r) => [r.taekus_card_uuid, r.memberName]));
      let startIndex = 0;
      for (let page = 0; page < 5; page++) {
        const body = await taekusRead('/api/banking/transactions/', {
          cardAccountUuid: TAEKUS_POOL_CARD_ACCOUNT,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          transactionType: 'ALL',
          startIndex: String(startIndex),
        });
        const txns = body?.transactions || [];
        txns.forEach((t) => {
          const amt = Number(t?.amount?.amount_usd ?? 0);
          const who = byUuid[t.card_uuid] || null;
          line(
            String(t.date || '').slice(0, 10) +
            ' · $' + amt.toLocaleString(undefined, { minimumFractionDigits: 2 }) +
            (t.refund ? ' (refund)' : '') +
            ' · ' + (t?.merchant?.name || t?.merchant?.original_description || '?') +
            ' · card ' + (t.card_nickname || '?') + (t.card_last_4 ? ' ·' + t.card_last_4 : '') +
            (who ? ' → ' + who : ' → (unmapped card)')
          );
        });
        if (!body?.is_more) break;
        startIndex = Number(body?.end_index ?? 0) + 1;
      }
      if (out.length === 1) line('No transactions in the window.');
      line(''); line('Done.');
    } catch (e) {
      line('error: ' + (e?.message || e));
    } finally {
      setDiagBusy(false);
    }
  };

  const loadCards = async () => {
    setBusy(true); setCardsErr(null);
    try {
      const read = taekusRead;
      // Card-account endpoints need an explicit cardAccountUuid: Taekus's
      // default-account lookup returns "No card account for this user" for
      // this login even though every account resolves when addressed
      // directly (2026-08-07 diagnostics). The pool's card list therefore
      // targets the pinned Corporate Purchasing account, with a self-healing
      // fallback that rediscovers a Corporate* account from
      // internal-accounts/ if the pinned uuid ever stops resolving.
      let body;
      try {
        body = await read('/api/banking/payment-cards/virtual/', { cardAccountUuid: TAEKUS_POOL_CARD_ACCOUNT });
      } catch (e) {
        const ia = await read('/api/banking/internal-accounts/').catch(() => null);
        const corp = (ia?.internal_accounts || []).find((a) => /corporate/i.test(a?.display_name || ''));
        if (!corp?.uuid || corp.uuid === TAEKUS_POOL_CARD_ACCOUNT) throw e;
        body = await read('/api/banking/payment-cards/virtual/', { cardAccountUuid: corp.uuid });
      }
      // The docs pin the response as an array of DisplayPaymentCard, but stay
      // tolerant of wrapper containers and field aliases anyway.
      const list = Array.isArray(body) ? body : (body?.results || body?.cards || body?.data || []);
      const mapped = list.map((c) => {
        const uuid = c.uuid || c.card_uuid || c.id || '';
        const name = String(c.nickname || c.name || '');
        const label = [name || null, c.last_four || c.last4 || c.pan_last_four]
          .filter(Boolean).join(' · ');
        return { uuid: String(uuid), name, label: label || String(uuid) };
      }).filter((c) => c.uuid);
      setCards(mapped);
      if (!mapped.length) setCardsErr('The Taekus API returned no virtual cards.');
    } catch (e) {
      setCards(null);
      setCardsErr(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async (acct) => {
    const raw = (drafts[acct.id] !== undefined ? drafts[acct.id] : (acct.taekus_card_uuid || '')).trim();
    const { error } = await sb.from('financial_accounts')
      .update({ taekus_card_uuid: raw || null })
      .eq('id', acct.id);
    if (error) {
      window.alert('Save failed: ' + (error.message || error) +
        (String(error.message || '').includes('uq_financial_accounts_taekus_card')
          ? '\n\nThat card UUID is already mapped to another account.' : ''));
      return;
    }
    setDrafts((d) => { const n = { ...d }; delete n[acct.id]; return n; });
    flash && flash(raw ? 'Card mapped' : 'Card unmapped');
    onChange && onChange();
  };

  // A second Taekus card for the same investor = a second TK-C account row.
  // The card→member lookup in the fetch reads every active row with a UUID,
  // so both cards attribute to the member; pool math filters on account kind
  // and rolls up per member, so nothing double-counts; TK-C rows are hidden
  // from investor account lists. Removal is offered only while the extra
  // row has no card, because a mapped row may be what the fetch's
  // late-mapping heal is about to stamp onto backlog evidence.
  const addSecondCard = async (a) => {
    const n = rows.filter((r) => r.member_id === a.member_id).length + 1;
    const { error } = await sb.from('financial_accounts').insert({
      member_id: a.member_id,
      account_kind: 'taekus_corp',
      institution: 'Taekus Corp',
      nickname: 'Card ' + n,
      notes: 'Extra TK-C card row added from the TK-C cards table. Draws from every card of this member book against their oldest TK-C account.',
    });
    if (error) { window.alert('Could not add a card row: ' + (error.message || error)); return; }
    flash && flash('Card row added for ' + a.memberName + ' — pick the card and Save');
    onChange && onChange();
  };
  const removeExtra = async (a) => {
    if (!a.isExtra || a.taekus_card_uuid) return;
    if (!window.confirm('Remove the unused extra card row for ' + a.memberName + '?')) return;
    const { error } = await sb.from('financial_accounts').delete().eq('id', a.id);
    if (error) { window.alert('Could not remove the row: ' + (error.message || error)); return; }
    flash && flash('Card row removed');
    onChange && onChange();
  };

  // Commit every filled draft in one pass (sequential so unique-index
  // collisions report per member instead of aborting the batch).
  const saveAllFilled = async () => {
    if (!filledRows.length) return;
    let okCount = 0;
    const errs = [];
    for (const a of filledRows) {
      const { error } = await sb.from('financial_accounts')
        .update({ taekus_card_uuid: drafts[a.id].trim() })
        .eq('id', a.id);
      if (error) errs.push(a.memberName + ': ' + (error.message || error));
      else okCount++;
    }
    if (errs.length) window.alert('Some saves failed:\n' + errs.join('\n'));
    if (okCount) flash && flash(okCount + ' card' + (okCount === 1 ? '' : 's') + ' mapped');
    setDrafts({});
    onChange && onChange();
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', boxSizing: 'border-box',
    border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 8,
    fontSize: 12.5, fontFamily: 'ui-monospace, monospace',
  };

  return (
    <section className="card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div className="card-eyebrow">Pool draw attribution</div>
          <h3 style={{ margin: '2px 0 6px', fontSize: 16, fontWeight: 800 }}>TK-C cards → members</h3>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2, #475569)', lineHeight: 1.5 }}>
            Each person's TK-C account carries the UUID of their Taekus virtual card, so a pool draw
            attributes to the person whose card moved while the balance pools at the household.
            Accounts are provisioned by the T-009 migration; map each one to a card below.
            Someone holding two Taekus cards gets a second row via <em>Add a second card</em> — both
            cards then attribute to them, and every draw still books against their original TK-C account.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <button type="button" className="btn-ghost" disabled={busy} onClick={loadCards}>
            {busy ? 'Loading…' : 'Load cards from Taekus'}
          </button>
          <button type="button" className="btn-ghost" disabled={diagBusy} onClick={runDiagnostics}>
            {diagBusy ? 'Probing…' : 'Run API diagnostics'}
          </button>
          <button type="button" className="btn-ghost" disabled={diagBusy} onClick={showActivity}>
            {diagBusy ? 'Probing…' : 'Recent card activity'}
          </button>
          {suggestionsToFill.length > 0 && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setDrafts((d) => {
                const n = { ...d };
                suggestionsToFill.forEach(([id, s]) => { n[id] = s.uuid; });
                return n;
              })}
            >
              Fill {suggestionsToFill.length} suggestion{suggestionsToFill.length === 1 ? '' : 's'}
            </button>
          )}
          {filledRows.length > 0 && (
            <button type="button" className="btn-ghost" onClick={saveAllFilled}>
              Save all filled ({filledRows.length})
            </button>
          )}
        </div>
      </div>

      {cardsErr && (
        <div style={{ margin: '8px 0', padding: '9px 11px', background: 'var(--rose-bg, #fef2f2)', border: '1px solid var(--rose-border, #fecaca)', borderRadius: 8, color: 'var(--rose, #e11d48)', fontSize: 12.5 }}>
          {cardsErr}
        </div>
      )}
      {diag && (
        <pre style={{ margin: '8px 0', padding: '10px 12px', background: 'var(--bg-2, #f8fafc)', border: '1px solid var(--border, #e5e7ef)', borderRadius: 8, fontSize: 11, lineHeight: 1.55, fontFamily: 'ui-monospace, monospace', maxHeight: 340, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--text, #1e293b)' }}>
          {diag}
        </pre>
      )}

      <DrawRecordingPanel />
      {cards && cards.length > 0 && (
        <datalist id="tkc-card-options">
          {cards.map((c) => <option key={c.uuid} value={c.uuid}>{c.label}</option>)}
        </datalist>
      )}

      {rows.length === 0 ? (
        <div style={{ padding: '14px 2px', color: 'var(--text-3, #94a3b8)', fontSize: 13 }}>
          No per-person TK-C accounts yet — apply <span style={{ fontFamily: 'ui-monospace, monospace' }}>migrations/2026-08-04-t009-pool-positions.sql</span> to provision them.
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {rows.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border, #e5e7ef)' }}>
              <div style={{ flex: '0 0 180px', fontSize: 13.5, fontWeight: 700 }}>
                {a.memberName}
                {a.isExtra && (
                  <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 800, color: 'var(--accent-2, #3730a3)', background: 'var(--accent-bg, #eef2ff)', border: '1px solid var(--accent-border, #c7d2fe)', borderRadius: 999, padding: '1px 7px', letterSpacing: '0.03em', verticalAlign: 'middle' }}>
                    {a.nickname || 'extra card'}
                  </span>
                )}
                {a.taekus_card_uuid && drafts[a.id] === undefined && (
                  <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--green, #059669)', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 2 }}>mapped</div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                {cards && cards.length ? (
                  // Owner report 2026-08-07: Kareem's own card is named
                  // "Initial card" — no resemblance to any member name, so
                  // the match engine never suggests it and the free-text
                  // datalist made manual entry unreliable. Once the card
                  // list is loaded, a plain dropdown replaces free text:
                  // every card is pickable; cards mapped or drafted to OTHER
                  // rows are hidden to pre-empt unique-index collisions.
                  <select
                    style={inputStyle}
                    value={drafts[a.id] !== undefined ? drafts[a.id] : (a.taekus_card_uuid || '')}
                    onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                  >
                    <option value="">— unmapped —</option>
                    {cards.filter((c) => {
                      const current = drafts[a.id] !== undefined ? drafts[a.id] : (a.taekus_card_uuid || '');
                      if (c.uuid === current) return true;
                      const mappedElsewhere = rows.some((r) => r.id !== a.id && r.taekus_card_uuid === c.uuid);
                      const draftedElsewhere = Object.entries(drafts).some(([id, v]) => id !== a.id && v === c.uuid);
                      return !mappedElsewhere && !draftedElsewhere;
                    }).map((c) => <option key={c.uuid} value={c.uuid}>{c.label}</option>)}
                  </select>
                ) : (
                  <input
                    style={inputStyle}
                    list="tkc-card-options"
                    placeholder="Taekus virtual-card UUID — or Load cards for a dropdown"
                    value={drafts[a.id] !== undefined ? drafts[a.id] : (a.taekus_card_uuid || '')}
                    onChange={(e) => setDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                  />
                )}
                {suggestions[a.id] && !a.taekus_card_uuid && unfilled(a.id) && (
                  <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--accent-2, #3730a3)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>Suggested: <strong>{suggestions[a.id].label}</strong> ({Math.round(suggestions[a.id].s * 100)}% name match)</span>
                    <button
                      type="button"
                      className="btn-ghost-sm"
                      onClick={() => setDrafts((d) => ({ ...d, [a.id]: suggestions[a.id].uuid }))}
                    >Use</button>
                  </div>
                )}
              </div>
              <button type="button" className="btn-ghost-sm" onClick={() => save(a)}>Save</button>
              {!a.isExtra && a.taekus_card_uuid && drafts[a.id] === undefined && (
                <button type="button" className="btn-ghost-sm" title={'Give ' + a.memberName + ' a second card row'} onClick={() => addSecondCard(a)}>+ Add a second card</button>
              )}
              {a.isExtra && !a.taekus_card_uuid && drafts[a.id] === undefined && (
                <button type="button" className="btn-ghost-sm" title="Remove this unused extra card row" onClick={() => removeExtra(a)}>Remove</button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function KindsTab({ kinds, steps = [], refs = [], loops = [], accounts = [], membersAll = [], onChange, flash }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  // kind slug → loop names (kind on either side of a step → loop_step_refs)
  // and → names of everyone holding an active financial account of the kind.
  const kindMaps = useMemo(() => {
    const loopName = Object.fromEntries(loops.map((l) => [l.id, l.name]));
    const stepLoops = {};
    refs.forEach((r) => { (stepLoops[r.step_id] = stepLoops[r.step_id] || []).push(r.loop_id); });
    const memberById = Object.fromEntries(membersAll.map((m) => [m.id, m]));
    const loopsByKind = {};
    steps.forEach((s) => {
      (stepLoops[s.id] || []).forEach((loopId) => {
        [s.source_kind, s.dest_kind].forEach((kn) => {
          if (!kn) return;
          (loopsByKind[kn] = loopsByKind[kn] || new Set()).add(loopName[loopId] || loopId);
        });
      });
    });
    const investorsByKind = {};
    accounts.forEach((a) => {
      if (!a.account_kind || a.active === false) return;
      const m = memberById[a.member_id];
      const label = m?.display_name || m?.name || a.member_id || '?';
      (investorsByKind[a.account_kind] = investorsByKind[a.account_kind] || new Set()).add(label);
    });
    return { loopsByKind, investorsByKind };
  }, [steps, refs, loops, accounts, membersAll]);

  return (
    <>
      <div className="cadm-toolbar">
        <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
          {kinds.length} account kind{kinds.length === 1 ? '' : 's'}. The slug is referenced by steps + financial accounts; the label is what investors see.
        </div>
        <button type="button" className="btn-primary cadm-new-btn" onClick={() => setCreating(true)}>+ New kind</button>
      </div>

      {creating && (
        <KindEditor
          kind={null}
          onCancel={() => setCreating(false)}
          onSaved={() => { setCreating(false); onChange(); flash('Account kind created.'); }}
          flash={flash}
        />
      )}

      <div className="cadm-table-wrap">
        <table className="cadm-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Loops</th>
              <th>Investors</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {kinds.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>No kinds defined.</td></tr>
            ) : kinds.map((k) => editing === k.name ? (
              <tr key={k.name}><td colSpan={5} style={{ padding: 0 }}>
                <KindEditor
                  kind={k}
                  onCancel={() => setEditing(null)}
                  onSaved={() => { setEditing(null); onChange(); flash('Kind saved.'); }}
                  flash={flash}
                />
              </td></tr>
            ) : (
              <KindRow
                key={k.name}
                kind={k}
                steps={steps}
                loopsIn={[...(kindMaps.loopsByKind[k.name] || [])].sort()}
                investors={[...(kindMaps.investorsByKind[k.name] || [])].sort()}
                onEdit={() => setEditing(k.name)}
                onChange={onChange}
                flash={flash}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function KindRow({ kind, steps = [], loopsIn = [], investors = [], onEdit, onChange, flash }) {
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    const sb = getSupabase();
    const { error } = await sb.from('inv_account_kinds').update({ active: !kind.active }).eq('name', kind.name);
    setSaving(false);
    if (error) { flash('Toggle failed: ' + (error.message || error)); return; }
    onChange();
  };
  // Hard delete, mirroring the legacy workbench guards: refuse while any
  // step or financial account still references the slug.
  const del = async () => {
    const stepRefs = steps.filter((s) => s.source_kind === kind.name || s.dest_kind === kind.name).length;
    if (stepRefs > 0) { flash(`Can't delete "${kind.label}" — referenced by ${stepRefs} step${stepRefs === 1 ? '' : 's'}. Use Deactivate instead, or remove those references first.`); return; }
    setSaving(true);
    const sb = getSupabase();
    const { count: acctCount, error: acctErr } = await sb.from('financial_accounts').select('id', { count: 'exact', head: true }).eq('account_kind', kind.name);
    if (acctErr) { setSaving(false); flash('Reference check failed: ' + (acctErr.message || acctErr)); return; }
    if ((acctCount || 0) > 0) { setSaving(false); flash(`Can't delete "${kind.label}" — ${acctCount} financial account${acctCount === 1 ? '' : 's'} use it. Use Deactivate instead.`); return; }
    if (!confirm(`Permanently delete "${kind.label}"?\n\nNothing currently references it, so this is safe. This can't be undone.`)) { setSaving(false); return; }
    const { error } = await sb.from('inv_account_kinds').delete().eq('name', kind.name);
    setSaving(false);
    if (error) { flash('Delete failed: ' + (error.message || error)); return; }
    flash('Account kind deleted.');
    onChange();
  };
  const color = kindColor(kind.name);
  return (
    <tr className={kind.active ? '' : 'is-inactive'}>
      <td>
        <div className="cadm-kind-chip" style={{ '--chip-color': color }}>
          <span className="cadm-kind-chip-tile">
            {kind.logo_url
              ? <img src={kind.logo_url} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.textContent = kindInitial(kind.label); }} />
              : kindInitial(kind.label)}
          </span>
          <span className="cadm-kind-chip-label">{kind.label}</span>
        </div>
      </td>
      <td>
        {loopsIn.length === 0 ? <span style={{ color: 'var(--text-3)' }}>—</span> : (
          <div className="cadm-step-loops">
            {loopsIn.map((n) => <span key={n} className="cadm-step-loop">{n}</span>)}
          </div>
        )}
      </td>
      <td>
        {investors.length === 0 ? <span style={{ color: 'var(--text-3)' }}>—</span> : (
          <div className="cadm-step-loops">
            {investors.map((n) => <span key={n} className="cadm-step-loop">{n}</span>)}
          </div>
        )}
      </td>
      <td><span className={'cadm-status-pill ' + (kind.active ? 'is-active' : 'is-inactive')}>{kind.active ? 'Active' : 'Inactive'}</span></td>
      <td className="cadm-row-actions">
        <button type="button" className="cadm-link" disabled={saving} onClick={onEdit}>Edit</button>
        <button type="button" className="cadm-link" disabled={saving} onClick={toggle}>{kind.active ? 'Deactivate' : 'Activate'}</button>
        <button type="button" className="cadm-link cadm-link-danger" disabled={saving} onClick={del}>Delete</button>
      </td>
    </tr>
  );
}

function KindEditor({ kind, onCancel, onSaved, flash }) {
  const isNew = !kind;
  const [label,   setLabel]   = useState(kind?.label || '');
  const [slug,    setSlug]    = useState(kind?.name  || '');
  const [logoUrl, setLogoUrl] = useState(kind?.logo_url || '');
  const [sortOrder, setSortOrder] = useState(String(kind?.sort_order ?? 100));
  const [saving, setSaving] = useState(false);

  const autoSlug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'kind';

  const save = async () => {
    if (!label.trim()) { flash('Label is required.'); return; }
    const finalSlug = (slug.trim() || autoSlug(label)).toLowerCase();
    setSaving(true);
    const sb = getSupabase();
    const payload = {
      label: label.trim(),
      logo_url: logoUrl.trim() || null,
      sort_order: parseInt(sortOrder || '100', 10) || 100,
    };
    const { error } = isNew
      ? await sb.from('inv_account_kinds').insert({ name: finalSlug, ...payload, active: true })
      : await sb.from('inv_account_kinds').update(payload).eq('name', kind.name);
    setSaving(false);
    if (error) { flash('Save failed: ' + (error.message || error)); return; }
    onSaved();
  };

  return (
    <div className="cadm-editor">
      <div className="cadm-editor-grid">
        <label><span>Display label</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} disabled={saving} placeholder="e.g. Apple Card" autoFocus />
        </label>
        <label><span>Slug {isNew && <em style={{ color: 'var(--text-3)' }}>(auto from label)</em>}</span>
          <input type="text" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} disabled={saving || !isNew} placeholder={autoSlug(label) || 'apple_card'} />
        </label>
        <label><span>Logo URL (optional)</span>
          <input type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} disabled={saving} placeholder="https://…" />
        </label>
        <label><span>Sort order</span>
          <input onWheel={(e) => e.currentTarget.blur()} type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} disabled={saving} />
        </label>
      </div>
      <div className="cadm-editor-actions">
        <button type="button" className="btn-ghost" disabled={saving} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : (isNew ? 'Create kind' : 'Save changes')}</button>
      </div>
    </div>
  );
}
